import { useEffect, useRef, useState, useCallback } from "react";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Mic, MicOff, Volume2, VolumeX } from "lucide-react";
import { getUserProfile } from "../../utils/auth";
import AudioVideo from "../../images/AvatarImages/Orbiee_3_video (2).mp4";
import IdleVideo from "../../images/AvatarImages/Orbiee_3_video (2).mp4";
import { convertBuffersToWAV } from "./AudioCode";
import {
  isValidTranscription,
  isEmptyTranscriptError,
} from "../../services/websocket";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import { useWishlist } from "../../context/WishlistContext";
import {
  connectAvatar,
  sendText,
} from "../../services/WebRtcConnection/RealTimeAvatarAPI";
import OrbieeFlow from "./OrbieeFlow";
import {
  buildChatBubbleTextsFromResponse,
  splitAssistantPartsForDisplay,
  subscribeOrbieeBackendWelcome,
  connectOrbieeChatSocket,
} from "../../services/websocket";
import { applyWishlistFromAgentResponse } from "../../utils/applyWishlistFromAgentResponse";
import { applyCartFromAgentResponse } from "../../utils/agentCartPayload";
import {
  setVirtualTryOnHoldAvatarMic,
  isVirtualTryOnHoldingAvatarMic,
} from "../../utils/virtualTryOnAvatarHold";
import {
  claimAssistantMode,
  releaseAssistantMode,
  subscribeAssistantMode,
  getAssistantMode,
  isAvatarMicAutoStartAllowed,
  isAvatarPlaybackSuppressed,
  isAvatarAudioMuted,
} from "../../services/assistantMode";
import {
  ANALYSER_FFT_SIZE,
  SCRIPT_PROCESSOR_BUFFER_SIZE,
  CHROMA_MIN_GREEN,
  CHROMA_DOMINANCE,
  CHROMA_SOFTNESS,
  SHIRT_RECOLOR_ENABLED,
  SHIRT_RECOLOR_RGB,
  SHIRT_REFERENCE_LUMA,
  SHIRT_SHADE_FLOOR,
  SHIRT_BLUE_START,
  SHIRT_BLUE_RAMP,
  SILENCE_THRESHOLD_START_MIC,
  SILENCE_THRESHOLD_TOGGLE_MIC,
  SPEECH_THRESHOLD_START_MIC,
  SPEECH_THRESHOLD_TOGGLE_MIC,
  SILENCE_DURATION_MS,
  STREAM_DONE_PLAYBACK_TAIL_MS,
} from "../../config/realTimeAvatarConfig";

/**
 * Display-only. Chroma key still uses each source's native videoWidth/videoHeight
 * on the canvas bitmap; this CSS fits that bitmap into the avatar frame.
 *
 * `contain`, not `fill`: stretching to the box distorted the avatar whenever the
 * frame's shape didn't match the source (browser zoom used to do exactly that).
 * The keyed frames are transparent, so a source of a different ratio simply
 * centres with clear space around it rather than showing letterbox bars.
 */
const AVATAR_CANVAS_DISPLAY_STYLE = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  aspectRatio: "unset",
  objectFit: "contain",
};

/**
 * How long the mic keeps its loading look after the agent socket opens while
 * the backend's connect-greeting is still to come. Not a deadline for the
 * greeting: it is LLM-written ("Welcome back, Ethan! I see…") and takes
 * seconds, and a 2.5 s deadline dropped it on a reload and on a fresh
 * sign-in alike (2026-10-28, twice). Past this the mic reads "Muted" and is
 * usable; a greeting that still arrives before the shopper starts anything
 * is spoken all the same and re-arms the mic.
 */
const BACKEND_WELCOME_LOADING_MS = 2500;

export default function ConnectToRealTimeAvatargetVideo({
  onAvatarVideoLoadingChange,
}) {
  // ── Video refs ──────────────────────────────────────────────────────────────
  // audioVideoRef  → intro mp4 played before login
  // mainVideoRef   → single video element used for BOTH idle loop and WebRTC avatar stream
  //                  It is always mounted when step === "idle" so srcObject is never lost.
  const audioVideoRef = useRef(null);
  const mainVideoRef  = useRef(null);
  const audioCanvasRef = useRef(null);
  const mainCanvasRef  = useRef(null);
  const rafRef = useRef(null);

  // Unused loading video refs kept for potential future use
  const loadingVideoRef  = useRef(null);
  const loadingCanvasRef = useRef(null);
  const loadingRafRef    = useRef(null);

  // ── Audio / mic refs ────────────────────────────────────────────────────────
  const audioContextRef    = useRef(null);
  const analyserRef        = useRef(null);
  const processorRef       = useRef(null);
  const sourceRef          = useRef(null);
  const streamRef          = useRef(null);
  const recordedBuffersRef = useRef([]);
  const silenceTimeoutRef  = useRef(null);
  const hasSpokenRef       = useRef(false);
  const micEnabledRef      = useRef(false);
  /**
   * The shopper is out of the conversation loop (mic button tapped off, or
   * voice mode / the chat took over). Blocks automatic mic reopen only — it
   * does not interrupt avatar speech already in flight. Cleared when the
   * shopper taps the mic button again. `micArmed` is the same fact as state,
   * for the button; the two are only ever set together.
   *
   * Armed at mount: the start sequence below always runs once the shopper
   * is signed in and always settles — a spoken welcome, whose end of speech
   * opens the mic, or no welcome, which disarms it ("Muted"). The button
   * shows its loading look meanwhile. Until 2026-10-28 this was keyed on a
   * once-per-tab flag so that a reload, which skipped the sequence, started
   * "Muted" instead of "Thinking…" with nothing pending (known issue B7,
   * 2026-10-20); the sequence no longer skips, so the flag is gone.
   */
  const [micArmed, setMicArmed] = useState(true);
  // Initial value only — kept in step with the state by setConversationStopped.
  const conversationStoppedRef = useRef(!micArmed);
  const setConversationStopped = useCallback((stopped) => {
    conversationStoppedRef.current = stopped;
    setMicArmed(!stopped);
  }, []);
  /**
   * Live input level → the mic button's halo, written straight to a CSS
   * custom property from the analyser loop. Not React state: that loop runs
   * every animation frame and this component is far too heavy to re-render
   * at that rate. `volume` is the analyser's mean byte magnitude (0–255);
   * speech at a normal distance sits around 20–60, so 48 saturates the halo.
   */
  const micBtnRef = useRef(null);
  const paintMicLevel = useCallback((volume) => {
    const btn = micBtnRef.current;
    if (!btn) return;
    const level = Math.min(1, Math.max(0, volume / 48));
    btn.style.setProperty("--ob-mic-level", level.toFixed(2));
  }, []);
  const playPromiseRef     = useRef(null);
  const webrtcStreamRef    = useRef(null); // live WebRTC MediaStream
  const videoLoadedRef     = useRef(false);
  const welcomeDoneRef     = useRef(false);
  const streamDoneTimerRef = useRef(null);
  const avatarWelcomeInFlightRef = useRef(false);
  // The pending greeting (start sequence): its welcome subscription, the
  // timer that ends the loading look, and whether a greeting has been
  // taken — one per page life.
  const pendingGreetingUnsubRef = useRef(null);
  const greetingSettleTimerRef = useRef(null);
  const greetingHandledRef = useRef(false);
  const onAvatarVideoLoadingChangeRef = useRef(onAvatarVideoLoadingChange);
  const isAvatarSpeakingRef = useRef(false);
  const vtoDeferredMicRef = useRef(false);
  const enableMicProgrammaticallyRef = useRef(null);
  const tryEnableMicAfterAvatarAndVtoRef = useRef(null);

  // ── Avatar speech queue refs ────────────────────────────────────────────────
  // speechQueueRef      → texts waiting to be spoken by the avatar (FIFO)
  // segmentInFlightRef  → a queued text has been sent and is being spoken
  // turnActiveRef       → a chat turn is still running on the backend
  // speechWatchdogRef   → failsafe timer in case stream/done never arrives
  const speechQueueRef = useRef([]);
  const segmentInFlightRef = useRef(false);
  const turnActiveRef = useRef(false);
  const speechWatchdogRef = useRef(null);
  const speakNextRef = useRef(null);
  const onAvatarSegmentEndRef = useRef(null);

  const cancelPendingStreamDone = useCallback(() => {
    if (streamDoneTimerRef.current) {
      clearTimeout(streamDoneTimerRef.current);
      streamDoneTimerRef.current = null;
    }
  }, []);

  // ── State ───────────────────────────────────────────────────────────────────
  const [step, setStep] = useState("audio"); // "audio" | "idle"
  // isAvatarSpeaking: true while the WebRTC avatar is streaming (or a video clip is playing)
  const [isAvatarSpeaking, setIsAvatarSpeaking] = useState(false);
  const [audioEnabled, setAudioEnabled]     = useState(false);
  /**
   * The shopper has used the speaker button or clicked the portrait — said
   * something about the speaker. Until then, arming the mic by hand also
   * turns the speaker on (toggleMic): a sign-in with no backend welcome
   * leaves `audioEnabled` false, and a shopper who then taps the mic to
   * talk expects to hear the reply — every reply, not only the first one,
   * which `ontrack` unmutes regardless; later ones are re-attached with
   * `muted = !audioEnabled`. A shopper who muted the speaker on purpose
   * keeps it muted.
   */
  const speakerTouchedRef = useRef(false);
  // The chat (or voice mode) holds the conversation, so the avatar must not
  // be heard — whatever the speaker preference above says. The register is
  // the truth (isAvatarAudioMuted); this is its copy for rendering, and the
  // imperative writes to `video.muted` read the register directly so they
  // cannot act on a render that has not happened yet.
  const [modeMuted, setModeMuted] = useState(() => isAvatarAudioMuted());
  const avatarAudible = audioEnabled && !modeMuted;
  const [micEnabled,   setMicEnabled]       = useState(false);
  const [isLoading,    setIsLoading]        = useState(false);
  const [isAvatarVideoLoading, setIsAvatarVideoLoading] = useState(false);

  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { addToCart, replaceCartItemsFromAgent } = useCart();
  const wishlist = useWishlist();

  const wishlistSyncRef = useRef(null);
  const cartSyncRef = useRef(null);
  useEffect(() => {
    // State only — OrbieeFlow owns navigation for the turn.
    wishlistSyncRef.current = (lastResponse) => {
      applyWishlistFromAgentResponse(lastResponse, wishlist);
    };
  }, [wishlist]);

  useEffect(() => {
    cartSyncRef.current = (lastResponse) => {
      applyCartFromAgentResponse(replaceCartItemsFromAgent, lastResponse);
    };
  }, [replaceCartItemsFromAgent]);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    onAvatarVideoLoadingChangeRef.current = onAvatarVideoLoadingChange;
  }, [onAvatarVideoLoadingChange]);

  useEffect(() => {
    isAvatarSpeakingRef.current = isAvatarSpeaking;
  }, [isAvatarSpeaking]);

  const setAvatarLoadingState = useCallback((val) => {
    setIsAvatarVideoLoading(val);
    if (onAvatarVideoLoadingChangeRef.current) {
      onAvatarVideoLoadingChangeRef.current(val);
    }
  }, []);

  const ensureAvatarConnected = useCallback(async () => {
    await connectAvatar(mainVideoRef);
  }, []);

  /**
   * Out of the loop, "Muted": nothing is pending that would open the mic
   * (so "Thinking…" would be a lie — B7), the loading look ends, and the
   * start sequence counts as done for the frame. A greeting subscription
   * still up is left alone on purpose: a greeting that still arrives before
   * the shopper starts anything re-arms the mic and is spoken.
   */
  const releaseMuted = useCallback((reason) => {
    console.info(`[Avatar] ${reason} — muted until the mic is tapped`);
    welcomeDoneRef.current = true;
    setConversationStopped(true);
    setIsLoading(false);
    setAvatarLoadingState(false);
  }, [setConversationStopped, setAvatarLoadingState]);

  /** Drop the greeting subscription and the loading-look timer, if any. */
  const cancelPendingGreeting = useCallback(() => {
    if (pendingGreetingUnsubRef.current) {
      try { pendingGreetingUnsubRef.current(); } catch (_) {}
      pendingGreetingUnsubRef.current = null;
    }
    if (greetingSettleTimerRef.current) {
      clearTimeout(greetingSettleTimerRef.current);
      greetingSettleTimerRef.current = null;
    }
  }, []);

  /**
   * The shopper started something (an assistant mode was claimed — chat,
   * voice, or the mic) while a greeting was still awaited: from now on it
   * would interrupt a conversation, so it is left to the chat thread, which
   * shows it anyway. A loading look still up ends now, not at its timer.
   */
  const settleGreetingWait = useCallback((reason) => {
    const loadingLookUp = !!greetingSettleTimerRef.current;
    cancelPendingGreeting();
    if (loadingLookUp) releaseMuted(reason);
  }, [cancelPendingGreeting, releaseMuted]);

  /* =========================
     🎨 CHROMA KEY
  ========================= */
  const applyChromaKey = (video, canvas) => {
    if (!video || !canvas || video.readyState < 2) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    let frame;
    try {
      frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
    } catch (e) {
      // Canvas tainted by cross-origin video — skip chroma key for this frame
      return;
    }
    const data  = frame.data;
    const [shirtR, shirtG, shirtB] = SHIRT_RECOLOR_RGB;
    const shadeScale = (1 - SHIRT_SHADE_FLOOR) / SHIRT_REFERENCE_LUMA;
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i], b = data[i + 2];
      let g = data[i + 1];
      const isGreen =
        g > CHROMA_MIN_GREEN &&
        g > r * CHROMA_DOMINANCE &&
        g > b * CHROMA_DOMINANCE;
      if (isGreen) {
        const alpha = Math.min(255, Math.max(0, (g - Math.max(r, b)) * CHROMA_SOFTNESS));
        data[i + 3] = 255 - alpha;
        g = Math.min(g, (r + b) / 2);
        data[i + 1] = g;
      }

      // Shirt recolor. The shirt is the only blue-dominant thing in frame —
      // skin, hair, lips and eyes all run r > b — so blueness alone masks it.
      // Blueness is taken relative to brightness so dark folds still qualify,
      // and it runs after the despill so green-edge fringe gets caught too.
      // The new colour is scaled by the pixel's own luma, keeping the folds.
      // Plain ifs over Math.min: this runs on ~a third of every frame's pixels.
      if (SHIRT_RECOLOR_ENABLED && b - r > 2) {
        let weight = ((b - r) / (b + 1) - SHIRT_BLUE_START) / SHIRT_BLUE_RAMP;
        if (weight > 0) {
          if (weight > 1) weight = 1;
          const shade =
            SHIRT_SHADE_FLOOR + (0.299 * r + 0.587 * g + 0.114 * b) * shadeScale;
          let nr = shirtR * shade, ng = shirtG * shade, nb = shirtB * shade;
          if (nr > 255) nr = 255;
          if (ng > 255) ng = 255;
          if (nb > 255) nb = 255;
          data[i]     = r + (nr - r) * weight;
          data[i + 1] = g + (ng - g) * weight;
          data[i + 2] = b + (nb - b) * weight;
        }
      }
    }
    ctx.putImageData(frame, 0, 0);
  };

  /* =========================
     🎞 FRAME PROCESSORS
  ========================= */
  const processFrame = useCallback(() => {
    const video  = step === "audio" ? audioVideoRef.current : mainVideoRef.current;
    const canvas = step === "audio" ? audioCanvasRef.current : mainCanvasRef.current;
    if (video && canvas && !video.paused) applyChromaKey(video, canvas);
    rafRef.current = requestAnimationFrame(processFrame);
  }, [step]);

  const processLoadingFrame = useCallback(() => {
    const video  = loadingVideoRef.current;
    const canvas = loadingCanvasRef.current;
    if (video && canvas && !video.paused) applyChromaKey(video, canvas);
    loadingRafRef.current = requestAnimationFrame(processLoadingFrame);
  }, []);

  /* =========================
     ▶️ VIDEO PLAY / PAUSE EVENTS → drive RAF
  ========================= */
  useEffect(() => {
    const video = step === "audio" ? audioVideoRef.current : mainVideoRef.current;
    if (!video) return;

    const onPlay  = () => { cancelAnimationFrame(rafRef.current); processFrame(); };
    const onPause = () => cancelAnimationFrame(rafRef.current);

    video.addEventListener("play",  onPlay);
    video.addEventListener("pause", onPause);
    // Kick off immediately if the video is already playing (e.g. WebRTC stream arrived early)
    if (!video.paused) { cancelAnimationFrame(rafRef.current); processFrame(); }

    return () => {
      video.removeEventListener("play",  onPlay);
      video.removeEventListener("pause", onPause);
      cancelAnimationFrame(rafRef.current);
    };
  }, [step, processFrame, user, isAvatarSpeaking]);

  /* =========================
     📹 IDLE LOOP — start looping IdleVideo on mainVideoRef
  ========================= */
  const startIdleLoop = useCallback(async () => {
    const video = mainVideoRef.current;
    if (!video) return;

    // Never clobber a live avatar stream (e.g. a stale delayed timer firing
    // right after the next queued response started speaking).
    if (isAvatarSpeakingRef.current) return;

    // Cancel any in-flight play()
    if (playPromiseRef.current) {
      playPromiseRef.current.catch(() => {});
      playPromiseRef.current = null;
    }
    try { video.pause(); } catch (_) {}

    // Clear WebRTC stream if any
    if (video.srcObject) video.srcObject = null;

    video.src   = IdleVideo;
    video.loop  = true;
    video.muted = !audioEnabled || isAvatarAudioMuted();

    // Wait until the browser has enough data
    if (video.readyState < 2) {
      await new Promise((resolve) => {
        const onReady = () => { video.removeEventListener("canplay", onReady); resolve(); };
        video.addEventListener("canplay", onReady);
        setTimeout(resolve, 2000);
      });
    }

    try {
      playPromiseRef.current = video.play();
      await playPromiseRef.current;
      playPromiseRef.current = null;
    } catch (err) {
      playPromiseRef.current = null;
      if (err.name !== "AbortError") console.warn("Idle loop play error:", err);
    }
  }, [audioEnabled]);

  /* =========================
     📹 INTRO VIDEO (before login)
  ========================= */
  useEffect(() => {
    if (!user && step === "audio" && audioVideoRef.current) {
      const video  = audioVideoRef.current;
      const canvas = audioCanvasRef.current;
      video.muted = true;
      video.pause();
      const drawFirstFrame = () => {
        if (video && canvas && video.readyState >= 2) {
          video.currentTime = 0;
          setTimeout(() => { if (video && canvas && video.readyState >= 2) applyChromaKey(video, canvas); }, 100);
        }
      };
      if (video.readyState >= 2) drawFirstFrame();
      else video.addEventListener("loadeddata", drawFirstFrame, { once: true });
      return () => video.removeEventListener("loadeddata", drawFirstFrame);
    }
  }, [user, step]);

  /* =========================
     👤 USER CHECK — skip audio intro when already logged in
  ========================= */
  useEffect(() => {
    if (user && step === "audio") setStep("idle");
  }, [user, step]);

  /* =========================
     ▶️ START IDLE LOOP WHEN SWITCHING TO IDLE STATE
  ========================= */
  useEffect(() => {
    if (step === "idle" && !isAvatarSpeaking) {
      // Small delay so React has time to mount mainVideoRef
      const t = setTimeout(() => startIdleLoop(), 300);
      return () => clearTimeout(t);
    }
  }, [step, isAvatarSpeaking, startIdleLoop]);

  /* =========================
     🔊 AUDIO ENABLE / TOGGLE
  ========================= */
  const enableAudio = async () => {
    speakerTouchedRef.current = true;
    if (audioEnabled) return;
    setAudioEnabled(true);
    const video = step === "audio" ? audioVideoRef.current : mainVideoRef.current;
    if (!video) return;
    if (playPromiseRef.current) { playPromiseRef.current.catch(() => {}); playPromiseRef.current = null; }
    // A click on the portrait records the preference; while the chat holds
    // the conversation it still makes no sound.
    video.muted  = isAvatarAudioMuted();
    video.volume = 1;
    try {
      playPromiseRef.current = video.play();
      await playPromiseRef.current;
      playPromiseRef.current = null;
    } catch (err) {
      playPromiseRef.current = null;
      if (err.name !== "AbortError") console.warn("enableAudio error:", err);
    }
  };

  const toggleAudio = async (e) => {
    e.stopPropagation();
    // The button is disabled in this state; this covers a tap that lands
    // before the render that disables it.
    if (isAvatarAudioMuted()) return;
    const video = step === "audio" ? audioVideoRef.current : mainVideoRef.current;
    if (!video) return;
    const next = !audioEnabled;
    speakerTouchedRef.current = true;
    setAudioEnabled(next);
    if (playPromiseRef.current) { playPromiseRef.current.catch(() => {}); playPromiseRef.current = null; }

    // For intro video before login: wait for the video to be ready before playing
    if (step === "audio" && !user && video.readyState < 2) {
      video.load();
      await new Promise((resolve) => {
        let done = false;
        const timeout = setTimeout(() => { if (!done) { done = true; resolve(); } }, 2000);
        const onOk  = () => { if (!done) { done = true; clearTimeout(timeout); video.removeEventListener("canplay", onOk); video.removeEventListener("error", onErr); resolve(); } };
        const onErr = () => { if (!done) { done = true; clearTimeout(timeout); video.removeEventListener("canplay", onOk); video.removeEventListener("error", onErr); resolve(); } };
        video.addEventListener("canplay", onOk);
        video.addEventListener("error", onErr);
      });
    }

    // Re-asked here: the intro clip's load above can take two seconds, and
    // the chat may have opened meanwhile.
    video.muted  = !next || isAvatarAudioMuted();
    video.volume = 1;
    try {
      if (step === "audio" && !user && next) {
        playPromiseRef.current = video.play();
        await playPromiseRef.current;
        playPromiseRef.current = null;
      } else if (video.paused) {
        playPromiseRef.current = video.play();
        await playPromiseRef.current;
        playPromiseRef.current = null;
      }
    } catch (err) {
      playPromiseRef.current = null;
      if (err.name !== "AbortError") console.warn("toggleAudio error:", err);
    }
  };

  /* =========================
     🗣 AVATAR SPEECH QUEUE
     Each llm_response streamed over the WebSocket is queued and spoken by the
     avatar in order — starting with the FIRST response, while the backend is
     still running tools / loading products. Between queued responses (and
     whenever nothing is being spoken) the idle loop plays so the avatar is
     never frozen on a still frame. The mic is re-enabled only after the turn
     completed AND the last queued response finished playing.
  ========================= */
  const tryEnableMicAfterAvatarAndVto = useCallback(() => {
    if (conversationStoppedRef.current || micEnabledRef.current) return;
    // Another surface (voice mode, the chat) owns the conversation: the
    // loop stays off until the shopper taps the avatar's mic by hand. Checked
    // before the deferrals below so nothing is queued to fire later.
    if (!isAvatarMicAutoStartAllowed()) return;

    // A chat turn is still running, or responses are still queued/speaking —
    // the mic comes back only after the LAST queued response has been spoken.
    if (
      turnActiveRef.current ||
      segmentInFlightRef.current ||
      speechQueueRef.current.length > 0
    ) {
      return;
    }

    if (isVirtualTryOnHoldingAvatarMic()) {
      vtoDeferredMicRef.current = true;
      console.debug("[VTO] deferring avatar mic — try-on image not loaded yet");
      return;
    }

    if (isAvatarSpeakingRef.current) {
      vtoDeferredMicRef.current = true;
      console.debug("[VTO] deferring avatar mic — avatar still speaking");
      return;
    }

    vtoDeferredMicRef.current = false;
    enableMicProgrammaticallyRef.current?.();
  }, []);

  const clearSpeechWatchdog = useCallback(() => {
    if (speechWatchdogRef.current) {
      clearTimeout(speechWatchdogRef.current);
      speechWatchdogRef.current = null;
    }
  }, []);

  /**
   * Switch the video element back to the looping idle clip (no mic change).
   * NOTE: Do NOT clear webrtcStreamRef here. The WebRTC peer connection and
   * its tracks remain LIVE after the avatar finishes speaking. We need the
   * stream reference to re-attach it for subsequent sendText calls, because
   * ontrack only fires once (on initial connection) — not on every message.
   */
  const goIdle = useCallback(async () => {
    setIsAvatarSpeaking(false);
    isAvatarSpeakingRef.current = false;
    welcomeDoneRef.current = true;

    const video = mainVideoRef.current;
    // Already playing the idle loop — don't restart it (avoids flicker).
    if (video && !video.srcObject && video.loop && video.src && !video.paused) {
      return;
    }

    if (video) {
      if (playPromiseRef.current) { playPromiseRef.current.catch(() => {}); playPromiseRef.current = null; }
      try { video.pause(); } catch (_) {}
      video.srcObject = null;
      video.src       = "";
    }

    await startIdleLoop();
  }, [startIdleLoop]);

  /** Idle loop + attempt to hand the mic back to the user. */
  const finishAvatarPlayback = useCallback(async () => {
    await goIdle();
    if (!micEnabledRef.current && !conversationStoppedRef.current) {
      tryEnableMicAfterAvatarAndVto();
    }
  }, [goIdle, tryEnableMicAfterAvatarAndVto]);

  /** Take the next queued text and have the avatar speak it. */
  const speakNextInQueue = useCallback(async () => {
    if (segmentInFlightRef.current) return;
    // Voice mode: the reply still reaches the chat thread (orbieeBackgroundChat),
    // but the hidden avatar must not speak it over the voice reply.
    if (isAvatarPlaybackSuppressed()) {
      speechQueueRef.current = [];
      return;
    }
    const text = speechQueueRef.current.shift();
    if (text == null) return;
    segmentInFlightRef.current = true;

    try {
      cancelPendingStreamDone();
      clearSpeechWatchdog();

      await ensureAvatarConnected();

      setIsAvatarSpeaking(true);
      isAvatarSpeakingRef.current = true;

      // ── CRITICAL for second+ responses ──────────────────────────────────
      // ontrack fires ONLY on the initial WebRTC connection, not on every
      // sendText call. If the video element was switched back to the idle
      // loop (src=IdleVideo), re-attach the stored live MediaStream so this
      // response is visible.
      if (webrtcStreamRef.current) {
        const vid = mainVideoRef.current;
        if (vid && vid.srcObject !== webrtcStreamRef.current) {
          if (playPromiseRef.current) { playPromiseRef.current.catch(() => {}); playPromiseRef.current = null; }
          vid.src       = "";   // clear idle loop source
          vid.loop      = false;
          vid.srcObject = webrtcStreamRef.current;
          vid.muted     = !audioEnabled || isAvatarAudioMuted();
          vid.volume    = 1;
          vid.play().catch((err) => { if (err.name !== "AbortError") console.warn("Re-attach play error:", err); });
        }
      }

      const videoResponse = await sendText(text);

      // Failsafe: if stream/done never arrives for this response, force the
      // queue forward so the avatar loop can never get stuck.
      const watchdogMs = Math.min(15000 + text.length * 80, 90000);
      speechWatchdogRef.current = setTimeout(() => {
        speechWatchdogRef.current = null;
        console.warn("⚠️ Avatar speech watchdog fired — forcing segment end");
        cancelPendingStreamDone();
        onAvatarSegmentEndRef.current?.();
      }, watchdogMs);

      // If the API returned a direct video URL, attach it; otherwise WebRTC handles it
      if (videoResponse) {
        const url = typeof videoResponse === "string"
          ? videoResponse
          : (videoResponse.videoUrl || videoResponse.video || videoResponse.url || null);
        if (url) {
          const video = mainVideoRef.current;
          if (video) {
            if (playPromiseRef.current) { playPromiseRef.current.catch(() => {}); playPromiseRef.current = null; }
            if (video.srcObject) video.srcObject = null;
            video.crossOrigin = "anonymous";
            video.src  = url;
            video.loop = false;
            video.muted = !audioEnabled || isAvatarAudioMuted();
            try {
              playPromiseRef.current = video.play();
              await playPromiseRef.current;
              playPromiseRef.current = null;
            } catch (err) {
              playPromiseRef.current = null;
              if (err.name !== "AbortError") console.warn("Video file play error:", err);
            }
          }
        }
      }
    } catch (err) {
      console.error("❌ Avatar speech error:", err);
      clearSpeechWatchdog();
      segmentInFlightRef.current = false;
      // Keep the loop alive: move on to the next queued response (or idle/mic).
      onAvatarSegmentEndRef.current?.();
    }
  }, [ensureAvatarConnected, audioEnabled, cancelPendingStreamDone, clearSpeechWatchdog]);

  /** Called when the avatar finished playing one queued response. */
  const onAvatarSegmentEnd = useCallback(async () => {
    clearSpeechWatchdog();
    segmentInFlightRef.current = false;

    // More responses queued → keep speaking.
    if (speechQueueRef.current.length > 0) {
      speakNextRef.current?.();
      return;
    }

    // Backend turn still running (products still loading) → play the idle
    // loop so the avatar is never stuck on a frozen frame; mic stays off.
    // The next llm_response will re-attach the stream and resume speaking.
    if (turnActiveRef.current) {
      await goIdle();
      return;
    }

    // Turn complete and every response spoken → idle loop + re-enable mic.
    await finishAvatarPlayback();
  }, [goIdle, finishAvatarPlayback, clearSpeechWatchdog]);

  useEffect(() => { speakNextRef.current = speakNextInQueue; }, [speakNextInQueue]);
  useEffect(() => { onAvatarSegmentEndRef.current = onAvatarSegmentEnd; }, [onAvatarSegmentEnd]);
  useEffect(() => () => clearSpeechWatchdog(), [clearSpeechWatchdog]);

  /** Add a text to the avatar speech queue and start speaking if idle. */
  const enqueueAvatarSpeech = useCallback((text) => {
    const t = String(text ?? "").trim();
    if (!t) return;
    speechQueueRef.current.push(t);
    if (!segmentInFlightRef.current) {
      speakNextRef.current?.();
    }
  }, []);

  /* =========================
     🎤 STOP MIC + PROCESS AUDIO
  ========================= */
  const stopMic = useCallback(async ({ skipProcessing = false } = {}) => {
    try {
      micEnabledRef.current = false;
      const buffers    = [...recordedBuffersRef.current];
      recordedBuffersRef.current = [];
      const sampleRate = audioContextRef.current?.sampleRate || 44100;

      try { processorRef.current?.disconnect(); sourceRef.current?.disconnect(); } catch (_) {}
      streamRef.current?.getTracks().forEach((t) => t.stop());
      if (audioContextRef.current) await audioContextRef.current.close().catch(() => {});
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
      processorRef.current = sourceRef.current = analyserRef.current = audioContextRef.current = streamRef.current = null;
      setMicEnabled(false);
      paintMicLevel(0);

      if (skipProcessing) { setIsLoading(false); return; }

      // User closed mic before speaking — skip STT and leave mic closed.
      // User must manually reopen the mic to start another turn.
      if (!hasSpokenRef.current) {
        console.debug("[Avatar] mic closed with no speech — skipping STT, mic stays off");
        setIsLoading(false);
        setIsAvatarSpeaking(false);
        isAvatarSpeakingRef.current = false;
        return;
      }

      if (buffers.length > 0) {
        let turnUserTextValid = false;
        try {
          setIsLoading(true);
          const wavBlob = convertBuffersToWAV(buffers, sampleRate);

          const userProfile = getUserProfile();
          const customerId  = userProfile?.customer_id;

          // ── Eager avatar speech ─────────────────────────────────────────────
          // Every llm_response streamed over the WebSocket is enqueued for the
          // avatar to speak IMMEDIATELY — while the backend is still running
          // tools / loading products — instead of waiting for the full turn.
          turnActiveRef.current = true;
          // Only responses the backend marked for speech (`announce` not
          // false) are collected here, so nothing else can reach the avatar.
          const llmParts = [];
          const enqueuedSegments = [];
          let transcriptOk = false;
          /** Responses the backend sent with `announce: false` this turn. */
          let withheldResponses = 0;

          const flushSegmentsToSpeech = () => {
            // Only speak once the server confirmed a valid transcription.
            if (!transcriptOk) return;
            const segments = splitAssistantPartsForDisplay(llmParts);
            for (let i = 0; i < segments.length; i++) {
              const seg = segments[i];
              if (i < enqueuedSegments.length) {
                // A streaming frame extended an earlier segment — update it in
                // the queue only if it hasn't been sent to the avatar yet.
                if (seg !== enqueuedSegments[i]) {
                  const qIdx = speechQueueRef.current.indexOf(enqueuedSegments[i]);
                  if (qIdx >= 0) speechQueueRef.current[qIdx] = seg;
                  enqueuedSegments[i] = seg;
                }
              } else {
                enqueuedSegments.push(seg);
                enqueueAvatarSpeech(seg);
              }
            }
          };

          let lastResponse = null;
          const ChartvertextResponse = await OrbieeFlow(
            { audioBlob: wavBlob },
            customerId,
            navigate,
            addToCart,
            "eng",
            (lr) => {
              if (typeof cartSyncRef.current === "function") {
                cartSyncRef.current(lr);
              }
              if (typeof wishlistSyncRef.current === "function") {
                wishlistSyncRef.current(lr);
              }
              lastResponse = lr;
            },
            {
              mode: "avatar",
              onTranscript: () => {
                transcriptOk = true;
                flushSegmentsToSpeech();
              },
              onLlmResponse: (text, _allParts, meta) => {
                // `announce: false` — the backend wants this one shown, not
                // said. It still reaches the chat thread through
                // orbieeBackgroundChat below; it just never enters the
                // speech queue, so it is never sent to the avatar API.
                if (meta?.announce === false) {
                  withheldResponses += 1;
                  return;
                }
                if (typeof text === "string" && text.trim()) {
                  llmParts.push(text);
                }
                flushSegmentsToSpeech();
              },
            }
          );

          // Backend turn finished — from here on, once the queue drains the
          // mic can be handed back to the user.
          turnActiveRef.current = false;

          const userText =
            lastResponse?.user_transcription ||
            lastResponse?.transcription ||
            "";
          turnUserTextValid = isValidTranscription(userText);

          if (turnUserTextValid) {
            const botText =
              ChartvertextResponse ||
              "Something went wrong, please try again.";
            const botTexts = lastResponse
              ? buildChatBubbleTextsFromResponse(lastResponse)
              : [botText];

            const intentRaw = lastResponse?.intent;
            const intents = Array.isArray(intentRaw)
              ? intentRaw.map(String)
              : intentRaw != null && intentRaw !== ""
                ? [String(intentRaw)]
                : [];
            if (intents.some((i) => i.includes("virtual_try_on"))) {
              setVirtualTryOnHoldAvatarMic(true);
            }

            window.dispatchEvent(
              new CustomEvent("orbieeBackgroundChat", {
                detail: {
                  userText,
                  botText,
                  botTexts: botTexts.length ? botTexts : [botText],
                  botPreambles: lastResponse?.llm_preamble_messages || [],
                },
              })
            );

            if (botText) {
              if (enqueuedSegments.length === 0 && withheldResponses === 0) {
                // No llm_response frames were streamed (fallback backends) —
                // speak the final merged text like before.
                enqueueAvatarSpeech(botText);
              } else if (enqueuedSegments.length === 0) {
                // The merged text contains the responses the backend withheld
                // from speech, so it must not be spoken as a fallback. Say
                // only what was marked for speech — often nothing, and then
                // the avatar stays quiet and the mic comes back below.
                transcriptOk = true;
                flushSegmentsToSpeech();
              } else {
                console.log("botText", botText);
              }
            }

            setIsLoading(false);

            // If the avatar already finished speaking every queued response (or
            // nothing was queued), close out the loop now — otherwise
            // onAvatarSegmentEnd does it after the last response finishes.
            if (!segmentInFlightRef.current && speechQueueRef.current.length === 0) {
              await finishAvatarPlayback();
            }
          } else {
            // STT returned nothing useful — clear any premature speech and reopen mic
            turnActiveRef.current = false;
            speechQueueRef.current = [];
            setIsLoading(false);
            setIsAvatarSpeaking(false);
            isAvatarSpeakingRef.current = false;
            tryEnableMicAfterAvatarAndVtoRef.current?.();
          }
        } catch (error) {
          turnActiveRef.current = false;
          speechQueueRef.current = [];
          if (isEmptyTranscriptError(error)) {
            console.debug("[Avatar] empty transcript — reopening mic");
          } else {
            console.error("❌ Voice chat error:", error);
          }
          setIsLoading(false);
          setIsAvatarSpeaking(false);
          isAvatarSpeakingRef.current = false;
          tryEnableMicAfterAvatarAndVtoRef.current?.();
        }
      } else {
        console.log("⚠️ No audio buffers recorded");
        setIsLoading(false);
        tryEnableMicAfterAvatarAndVtoRef.current?.();
      }
    } catch (err) {
      console.error("Stop mic error:", err);
    }
  }, [navigate, addToCart, enqueueAvatarSpeech, finishAvatarPlayback, paintMicLevel]);

  useEffect(() => {
    tryEnableMicAfterAvatarAndVtoRef.current = tryEnableMicAfterAvatarAndVto;
  }, [tryEnableMicAfterAvatarAndVto]);

  /* =========================
     🎤 ENABLE MIC PROGRAMMATICALLY
  ========================= */
  const enableMicProgrammatically = useCallback(async () => {
    if (conversationStoppedRef.current || micEnabledRef.current) return;
    if (!isAvatarMicAutoStartAllowed()) return;
    if (isVirtualTryOnHoldingAvatarMic()) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // The shopper may have started voice mode or opened the chat while the
      // permission prompt was up. Their action wins: release the device.
      if (conversationStoppedRef.current || !isAvatarMicAutoStartAllowed()) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      sourceRef.current = source;
      const processor = audioContext.createScriptProcessor(SCRIPT_PROCESSOR_BUFFER_SIZE, 1, 1);
      processorRef.current = processor;
      recordedBuffersRef.current = [];
      processor.onaudioprocess = (e) => {
        if (!micEnabledRef.current) return;
        recordedBuffersRef.current.push(new Float32Array(e.inputBuffer.getChannelData(0)));
      };
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = ANALYSER_FFT_SIZE;
      analyser.smoothingTimeConstant = 0;
      analyserRef.current = analyser;
      source.connect(processor);
      source.connect(analyser);
      processor.connect(audioContext.destination);
      hasSpokenRef.current = false;
      micEnabledRef.current = true;
      setMicEnabled(true);
      setIsLoading(false);
      claimAssistantMode("avatar", "avatar-mic-auto");

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const checkSilence = () => {
        if (!micEnabledRef.current) return;
        analyser.getByteFrequencyData(dataArray);
        const volume = dataArray.reduce((s, v) => s + v, 0) / dataArray.length;
        paintMicLevel(volume);
        if (volume > SPEECH_THRESHOLD_START_MIC) {
          hasSpokenRef.current = true;
          clearTimeout(silenceTimeoutRef.current);
          silenceTimeoutRef.current = null;
        } else if (volume < SILENCE_THRESHOLD_START_MIC && hasSpokenRef.current) {
          if (!silenceTimeoutRef.current) {
            silenceTimeoutRef.current = setTimeout(() => stopMic(), SILENCE_DURATION_MS);
          }
        }
        requestAnimationFrame(checkSilence);
      };
      checkSilence();
    } catch (err) {
      console.error("Mic access denied:", err);
    }
  }, [stopMic, paintMicLevel]);

  useEffect(() => {
    enableMicProgrammaticallyRef.current = enableMicProgrammatically;
  }, [enableMicProgrammatically]);

  useEffect(() => {
    const onVtoMicReleased = () => {
      tryEnableMicAfterAvatarAndVto();
    };
    window.addEventListener(
      "orbieeVirtualTryOnAvatarMicReleased",
      onVtoMicReleased
    );
    return () =>
      window.removeEventListener(
        "orbieeVirtualTryOnAvatarMicReleased",
        onVtoMicReleased
      );
  }, [tryEnableMicAfterAvatarAndVto]);

  /* =========================
     🎤 MIC BUTTON — in or out of the loop
     One button, two states. Armed: the mic is live, or will open by itself
     when the avatar finishes speaking. Tapping it while armed takes the
     shopper out of the loop — whatever the mic has captured is discarded,
     nothing reaches the backend, and no automatic reopen follows. Tapping
     it again arms the loop: if the avatar is idle the mic opens now, if it
     is busy the loop opens it after the reply, as it always did. This is
     what the separate "stop" button used to do, so that button is gone.
     Usable while the avatar speaks, since leaving mid-reply is the point.
  ========================= */
  const toggleMic = async (e) => {
    e.stopPropagation();
    if (isAvatarVideoLoading) return;

    if (!conversationStoppedRef.current) {
      setConversationStopped(true);
      if (micEnabledRef.current) {
        await stopMic({ skipProcessing: true });
      }
      releaseAssistantMode("avatar", "avatar-mic-off");
      return;
    }

    // Back in. Claimed now, so the loop is allowed to open the mic even if
    // the chat panel is open (an open panel otherwise holds the mode).
    setConversationStopped(false);
    claimAssistantMode("avatar", "avatar-mic-manual");
    // Starting to talk is also the moment to be heard — unless the shopper
    // has muted the speaker themselves (speakerTouchedRef).
    if (!audioEnabled && !speakerTouchedRef.current) setAudioEnabled(true);

    // Busy: finishAvatarPlayback hands the mic over when the reply ends.
    if (
      isLoading ||
      isAvatarSpeakingRef.current ||
      segmentInFlightRef.current ||
      turnActiveRef.current
    ) {
      return;
    }
    // A try-on render is still loading; its release event opens the mic.
    if (isVirtualTryOnHoldingAvatarMic()) {
      vtoDeferredMicRef.current = true;
      return;
    }

    if (!micEnabledRef.current) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Stood down (voice mode / chat took over) while the prompt was up.
        if (conversationStoppedRef.current) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = audioContext;
        const source = audioContext.createMediaStreamSource(stream);
        sourceRef.current = source;
        const processor = audioContext.createScriptProcessor(SCRIPT_PROCESSOR_BUFFER_SIZE, 1, 1);
        processorRef.current = processor;
        recordedBuffersRef.current = [];
        processor.onaudioprocess = (ev) => {
          if (!micEnabledRef.current) return;
          recordedBuffersRef.current.push(new Float32Array(ev.inputBuffer.getChannelData(0)));
        };
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = ANALYSER_FFT_SIZE;
        analyser.smoothingTimeConstant = 0;
        analyserRef.current = analyser;
        source.connect(processor);
        source.connect(analyser);
        processor.connect(audioContext.destination);
        hasSpokenRef.current = false;
        micEnabledRef.current = true;
        setMicEnabled(true);

        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        const checkSilence = () => {
          if (!micEnabledRef.current) return;
          analyser.getByteFrequencyData(dataArray);
          const volume = dataArray.reduce((s, v) => s + v, 0) / dataArray.length;
          paintMicLevel(volume);
          if (volume > SPEECH_THRESHOLD_TOGGLE_MIC) {
            hasSpokenRef.current = true;
            clearTimeout(silenceTimeoutRef.current);
            silenceTimeoutRef.current = null;
          } else if (volume < SILENCE_THRESHOLD_TOGGLE_MIC && hasSpokenRef.current) {
            if (!silenceTimeoutRef.current) {
              silenceTimeoutRef.current = setTimeout(() => stopMic(), SILENCE_DURATION_MS);
            }
          }
          requestAnimationFrame(checkSilence);
        };
        checkSilence();
      } catch (err) {
        console.error("Mic access denied:", err);
        // Nothing is listening and nothing will: show it.
        setConversationStopped(true);
        releaseAssistantMode("avatar", "avatar-mic-denied");
      }
    }
  };

  /* =========================
     🤝 ONE SURFACE AT A TIME
     Voice mode or the chat panel took the conversation: the ambient mic
     loop stops (any capture in progress is discarded, nothing is sent) and
     stays off until the shopper taps the avatar's mic again. Voice mode
     also silences the avatar — it is hidden there, and its stream would
     talk over the voice reply — by detaching the live stream and dropping
     queued speech. The WebRTC connection itself is kept: it is needed the
     moment the shopper comes back to the avatar.
     The chat panel silences it differently: the avatar stays on screen, so
     its stream stays attached and is only muted — a reply in mid-sentence,
     or one still coming back from the backend, plays out with no sound
     while the same words arrive in the thread.
  ========================= */
  useEffect(() => {
    return subscribeAssistantMode(({ mode, prev }) => {
      // A greeting still awaited is not for the avatar any more.
      if (mode !== "none") settleGreetingWait("the shopper started before the backend welcome");
      // Mute on the element now, not on the next render: the shopper must
      // not hear the rest of the word. Un-muting is left to the effect
      // below, which knows the speaker preference.
      const muted = isAvatarAudioMuted();
      setModeMuted(muted);
      if (muted) {
        [mainVideoRef.current, audioVideoRef.current].forEach((v) => {
          if (v) v.muted = true;
        });
      }

      if (mode === "voice" || mode === "chat") {
        setConversationStopped(true);
        vtoDeferredMicRef.current = false;
        if (micEnabledRef.current) {
          stopMic({ skipProcessing: true });
        }
      }

      if (mode === "voice") {
        speechQueueRef.current = [];
        const video = mainVideoRef.current;
        if (video && video.srcObject) {
          if (playPromiseRef.current) { playPromiseRef.current.catch(() => {}); playPromiseRef.current = null; }
          try { video.pause(); } catch (_) {}
          // webrtcStreamRef keeps the stream for the next avatar turn;
          // stream/done for the segment still arrives and settles the queue.
          video.srcObject = null;
        }
        return;
      }

      // Back from voice mode: the frame is blank (stream detached) unless a
      // segment is still being counted down, which goIdle restores itself.
      if (prev === "voice" && !isAvatarSpeakingRef.current && !segmentInFlightRef.current) {
        startIdleLoop();
      }
    });
  }, [stopMic, startIdleLoop, setConversationStopped, settleGreetingWait]);

  // One place that settles the element's mute from its two inputs — the
  // shopper's speaker preference and the register — whenever either changes.
  // This is what brings the sound back when the chat closes or the avatar's
  // mic is tapped, including in the middle of a reply.
  useEffect(() => {
    const video = step === "audio" ? audioVideoRef.current : mainVideoRef.current;
    if (video) video.muted = !avatarAudible;
  }, [avatarAudible, step]);

  /* =========================
     📡 STREAM START — WebRTC begins sending frames
  ========================= */
  useEffect(() => {
    const handleStreamStart = (event) => {
      cancelPendingStreamDone();
      setIsLoading(false);

      if (event.detail?.stream) {
        webrtcStreamRef.current = event.detail.stream;
        const video = mainVideoRef.current;
        if (video && isAvatarPlaybackSuppressed()) {
          // Voice mode: keep the stream for later, but never on the element —
          // an attached stream autoplays, and the API unmutes it.
          if (video.srcObject) {
            try { video.pause(); } catch (_) {}
            video.srcObject = null;
          }
        } else if (video) {
          // Attach the WebRTC stream to the video element
          if (playPromiseRef.current) { playPromiseRef.current.catch(() => {}); playPromiseRef.current = null; }
          video.src   = ""; // clear any idle loop src
          video.loop  = false;
          video.srcObject = event.detail.stream;
          video.muted     = !audioEnabled || isAvatarAudioMuted();
          video.volume    = 1;
          video.play().catch((err) => {
            if (err.name !== "AbortError") console.warn("WebRTC stream play error:", err);
          });
        }
      }

      setIsAvatarSpeaking(true);
      videoLoadedRef.current = true;
      setAvatarLoadingState(false);
    };

    window.addEventListener("avatarStreamStart", handleStreamStart);
    return () => window.removeEventListener("avatarStreamStart", handleStreamStart);
  }, [audioEnabled, setAvatarLoadingState, cancelPendingStreamDone]);

  /* =========================
     📡 STREAM DONE — avatar finished one response → next in queue or idle loop
  ========================= */
  useEffect(() => {
    const handleStreamDone = () => {
      // stream/done can fire twice (server + client data channels) — schedule once.
      if (streamDoneTimerRef.current) return;
      clearSpeechWatchdog();

      const video = mainVideoRef.current;
      const isWebRtcPlayback = !!(video?.srcObject);
      const tailMs = isWebRtcPlayback ? STREAM_DONE_PLAYBACK_TAIL_MS : 0;

      streamDoneTimerRef.current = setTimeout(() => {
        streamDoneTimerRef.current = null;
        onAvatarSegmentEnd();
      }, tailMs);
    };

    window.addEventListener("avatarStreamDone", handleStreamDone);
    return () => {
      window.removeEventListener("avatarStreamDone", handleStreamDone);
      cancelPendingStreamDone();
    };
  }, [onAvatarSegmentEnd, cancelPendingStreamDone, clearSpeechWatchdog]);

  /* =========================
     🚀 START AVATAR (signed in: at sign-in, or a page load with a session)
     The welcome is the backend's connect-greeting or nothing (2026-10-28;
     the app used to speak hardcoded copy when none arrived, and skipped the
     whole sequence after a reload). The backend decides, by greeting or
     not, on each new connection; this runs once per page life, for the
     page's first connection — a greeting the socket re-sends on a later
     reconnect is not spoken (nor shown: AvatarLogoCode only opens an empty
     thread with one).
     The greeting is LLM-written and takes seconds, so it is not awaited
     against a deadline: the mic keeps its loading look for
     BACKEND_WELCOME_LOADING_MS after the socket opens, then reads "Muted"
     and is usable, and the greeting is spoken whenever it arrives — as long
     as the shopper has not started anything meanwhile (no assistant mode
     claimed: no chat, no voice, no mic tap). With a greeting: WebRTC
     connects, the avatar speaks it, and its end of speech opens the mic.
     Without one: no avatar session is opened (a paid resource, for a
     shopper who may never talk), nothing is spoken, and the mic stays out
     of the loop until the shopper taps it; the first reply then connects
     the avatar lazily.
  ========================= */
  useEffect(() => {
    if (!user || loading) return;
    if (avatarWelcomeInFlightRef.current) return;
    avatarWelcomeInFlightRef.current = true;

    const speakGreeting = async (text) => {
      try {
        // Back in the loop (the loading look may already have given way to
        // "Muted"), and the loading look while the avatar connects —
        // avatarStreamStart clears it.
        setConversationStopped(false);
        setIsLoading(true);
        videoLoadedRef.current = false;
        setAvatarLoadingState(true);

        // 1. Establish WebRTC connection (stream will come via avatarStreamStart event)
        await ensureAvatarConnected();

        // 2. Mark avatar as speaking and send the welcome text
        cancelPendingStreamDone();
        setIsAvatarSpeaking(true);
        await sendText(text);
        setAudioEnabled(true);

        // Fallback: if avatarStreamStart never fires, clear loading after 5 s
        setTimeout(() => {
          if (!videoLoadedRef.current) setAvatarLoadingState(false);
        }, 5000);
      } catch (err) {
        console.error("❌ Avatar init error:", err);
        setIsAvatarSpeaking(false);
        // Nothing will open the mic now: out of the loop rather than
        // "Thinking…" for ever. A tap starts the conversation, and the
        // first reply retries the connection.
        releaseMuted("avatar connection failed");
      }
    };

    const startAvatar = async () => {
      try {
        setIsLoading(true);
        videoLoadedRef.current = false;
        setAvatarLoadingState(true);

        // The greeting rides on the agent socket. Sign-in awaited its open
        // before setting `user`; a page load did not (initializeAuth
        // connects in the background), so wait for the open here — the
        // call is idempotent. Same condition as AuthContext: a profile
        // without a customer id (an admin) has no agent socket, so no
        // greeting is coming.
        const socketOpen = user.customer_id
          ? await connectOrbieeChatSocket(user.customer_id)
          : false;
        if (!socketOpen) {
          releaseMuted("no agent socket, so no backend welcome");
          return;
        }
        if (getAssistantMode() !== "none") {
          // Started something while the socket opened (a restored chat
          // panel claims `chat` at mount): the greeting is not for the
          // avatar now — the thread shows it.
          releaseMuted("the shopper started before the backend welcome");
          return;
        }

        // Replays a greeting already cached (the usual sign-in case), else
        // fires when it arrives. The first one only.
        const unsub = subscribeOrbieeBackendWelcome((text) => {
          const greeting = String(text ?? "").trim();
          if (!greeting || greetingHandledRef.current) return;
          greetingHandledRef.current = true;
          cancelPendingGreeting();
          if (getAssistantMode() !== "none") {
            console.info("[Avatar] backend welcome arrived after the shopper started — not spoken");
            return;
          }
          speakGreeting(greeting);
        });
        if (greetingHandledRef.current) {
          // Replayed synchronously from the cache.
          unsub();
          return;
        }
        pendingGreetingUnsubRef.current = unsub;

        // The loading look only for the usual case; past it the mic is
        // usable, and a greeting that still comes re-arms it.
        greetingSettleTimerRef.current = setTimeout(() => {
          greetingSettleTimerRef.current = null;
          releaseMuted(`no backend welcome within ${BACKEND_WELCOME_LOADING_MS} ms`);
        }, BACKEND_WELCOME_LOADING_MS);
      } catch (err) {
        console.error("❌ Avatar start error:", err);
        cancelPendingGreeting();
        releaseMuted("the start sequence failed");
      }
    };

    startAvatar();
  }, [
    user,
    loading,
    ensureAvatarConnected,
    setAvatarLoadingState,
    cancelPendingStreamDone,
    setConversationStopped,
    releaseMuted,
    cancelPendingGreeting,
  ]);

  useEffect(() => {
    if (!user) {
      avatarWelcomeInFlightRef.current = false;
    }
  }, [user]);

  /* =========================
     🎞 LOADING VIDEO RAF
  ========================= */
  useEffect(() => {
    const video = loadingVideoRef.current;
    if (!video || !isLoading) return;
    const onPlay  = () => { cancelAnimationFrame(loadingRafRef.current); processLoadingFrame(); };
    const onPause = () => cancelAnimationFrame(loadingRafRef.current);
    video.addEventListener("play",  onPlay);
    video.addEventListener("pause", onPause);
    return () => {
      video.removeEventListener("play",  onPlay);
      video.removeEventListener("pause", onPause);
      cancelAnimationFrame(loadingRafRef.current);
    };
  }, [isLoading, processLoadingFrame]);

  /**
   * The mic button's four looks. "live" is the only one where speaking does
   * anything; "wait" is armed but the avatar is still talking or thinking,
   * so the mic opens by itself in a moment; "off" needs a tap; "loading" is
   * the avatar video still arriving after sign-in.
   */
  const micState = isAvatarVideoLoading
    ? "loading"
    : !micArmed
      ? "off"
      : micEnabled
        ? "live"
        : "wait";

  /* =========================
     🖼 RENDER
  ========================= */
  return (
    <Box
     onClick={enableAudio}
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        // The frame around it decides: pointer on phones, grab on desktop.
        cursor: "inherit",
        overflow: "hidden",
        borderRadius: "12px",
        transition: "box-shadow 0.3s ease",
        animation: isLoading && welcomeDoneRef.current
          ? "avatarBoxGlow 1.8s ease-in-out infinite"
          : "none",
        "@keyframes avatarBoxGlow": {
          "0%, 100%": {
            boxShadow:
              "inset 0 0 12px 3px rgba(44, 36, 82, 0.45), inset 0 0 24px 6px rgba(191, 238, 255, 0.3)",
          },
          "50%": {
            boxShadow:
              "inset 0 0 20px 8px rgba(44, 36, 82, 0.75), inset 0 0 40px 16px rgba(191, 238, 255, 0.5), inset 0 0 60px 24px rgba(227, 247, 255, 0.25)",
          },
        },
      }}
    >
      {/* ── AUDIO / INTRO VIDEO (before login) ───────────────────────────── */}
      {step === "audio" && !user && (
        <>
          <video
            ref={audioVideoRef}
            src={AudioVideo}
            crossOrigin="anonymous"
            muted
            playsInline
            preload="auto"
            onEnded={() => setStep("idle")}
            onLoadedData={() => {
              const video  = audioVideoRef.current;
              const canvas = audioCanvasRef.current;
              if (video && !audioEnabled) video.pause();
              if (video && canvas && video.readyState >= 2) {
                video.currentTime = 0;
                setTimeout(() => { if (video && canvas && video.readyState >= 2) applyChromaKey(video, canvas); }, 100);
              }
            }}
            style={{ display: "none" }}
          />
          <canvas ref={audioCanvasRef} style={AVATAR_CANVAS_DISPLAY_STYLE} />
        </>
      )}

      {/* ── IDLE / AVATAR VIDEO (after login)
            mainVideoRef is ALWAYS in the DOM when step === "idle".
            In idle mode   → src = IdleVideo, loop = true
            In avatar mode → srcObject = WebRTC stream (set by connectAvatar's ontrack handler)
            Having a single persistent element means the WebRTC srcObject is never lost.
      ──────────────────────────────────────────────────────────────────────── */}
      {step === "idle" && (
        <>
          <video
            ref={mainVideoRef}
            crossOrigin="anonymous"
            autoPlay
            muted={!avatarAudible}
            playsInline
            preload="auto"
            onLoadedData={async () => {
              const video = mainVideoRef.current;
              if (!video) return;
              if (playPromiseRef.current) { playPromiseRef.current.catch(() => {}); playPromiseRef.current = null; }
              try {
                video.muted = !audioEnabled || isAvatarAudioMuted();
                playPromiseRef.current = video.play();
                await playPromiseRef.current;
                playPromiseRef.current = null;
              } catch (err) {
                playPromiseRef.current = null;
                if (err.name !== "AbortError") console.warn("mainVideoRef onLoadedData play error:", err);
              }
              if (!videoLoadedRef.current) { videoLoadedRef.current = true; setAvatarLoadingState(false); }
            }}
            onEnded={() => {
              // Video-file mode: clip ended → return to idle loop
              if (!mainVideoRef.current?.loop) {
                setIsAvatarSpeaking(false);
                window.dispatchEvent(new CustomEvent("avatarStreamDone"));
              }
            }}
            style={{ display: "none" }}
          />
          <canvas ref={mainCanvasRef} style={AVATAR_CANVAS_DISPLAY_STYLE} />
        </>
      )}

      {/* ── CONTROLS — a slim solid capsule at the foot of the portrait
            (see .ob-avbar in orbiee.css): mic, status word, hairline,
            speaker. Mic: in / out of the conversation loop, with a ring
            while it is actually capturing. Speaker: the avatar's voice.
            Signed-in shoppers only — a signed-out visitor has no
            conversation to control, and a lone speaker button over the
            portrait looked like a fault. The intro clip is still started
            by clicking the portrait itself (enableAudio, above). ────────── */}
      {user && (
        <div className="ob-avbar" data-no-drag>
          <button
            ref={micBtnRef}
            type="button"
            className={`ob-avbtn ob-avbtn-${micState}`}
            onClick={toggleMic}
            disabled={micState === "loading"}
            aria-pressed={micArmed}
            aria-label={
              micArmed
                ? "Mute microphone and leave the conversation"
                : "Unmute microphone and start talking"
            }
            title={
              micArmed
                ? "Mute — leave the conversation"
                : "Unmute — start talking"
            }
          >
            {micArmed ? (
              <Mic size={15} strokeWidth={1.75} />
            ) : (
              <MicOff size={15} strokeWidth={1.75} />
            )}
            {micState === "live" && (
              <span className="ob-avbtn-ping" aria-hidden="true" />
            )}
          </button>
          {/* The word does the disambiguating: a blue mic that is not
              listening yet and a green one that is looked alike. "wait"
              reads two ways — the reply is still being worked out, or the
              avatar is saying it — but the button keeps one look for both. */}
          <span
            className={`ob-avlabel ob-avlabel-${micState}`}
            aria-live="polite"
          >
            {micState === "live"
              ? "Listening"
              : micState === "wait"
                ? isAvatarSpeaking
                  ? "Speaking"
                  : "Thinking…"
                : micState === "off"
                  ? "Muted"
                  : ""}
          </span>
          <span className="ob-avsep" aria-hidden="true" />
          {/* While the chat holds the conversation the avatar is silent and
              this cannot turn it back on — it would talk over the thread.
              The icon shows what is heard, not the saved preference, which
              returns untouched when the mode moves on. */}
          <button
            type="button"
            className="ob-avbtn"
            onClick={toggleAudio}
            disabled={modeMuted}
            aria-pressed={avatarAudible}
            aria-label={
              modeMuted
                ? "Avatar is muted while the chat is open"
                : audioEnabled
                  ? "Mute avatar"
                  : "Unmute avatar"
            }
            title={
              modeMuted
                ? "Muted while the chat is open"
                : audioEnabled
                  ? "Mute avatar"
                  : "Unmute avatar"
            }
          >
            {avatarAudible ? (
              <Volume2 size={15} strokeWidth={1.75} />
            ) : (
              <VolumeX size={15} strokeWidth={1.75} />
            )}
          </button>
        </div>
      )}

      {/* ── LOADER OVERLAY ───────────────────────────────────────────────── */}
     
    </Box>
  );
}
